import { useQueries } from '@tanstack/react-query';
import { ApiIncome } from 'src/cashflows/hooks/useIncomes';
import { LifeEvent } from 'src/cashflows/hooks/useLifeEvents';
import { FamilyMember } from './useFamilyMembers';
import {
  aggregateFamilyExpenditures,
  aggregateFamilyIncomes,
  aggregateFamilyLifeEvents,
  ApiExpenditure,
  FamilyExpenditure,
  FamilyIncome,
  FamilyLifeEvent,
} from './familyCashflowsAggregation';

export type { FamilyIncome, FamilyExpenditure, FamilyLifeEvent };

export function useFamilyCashflows(members: FamilyMember[]): {
  incomes: FamilyIncome[];
  expenditures: FamilyExpenditure[];
  lifeEvents: FamilyLifeEvent[];
  isLoading: boolean;
  hasErrors: boolean;
} {
  const incomeResults = useQueries({
    queries: members.map(member => ({
      queryKey: ['clients', member.clientId, 'incomes'],
      enabled: !!member.clientId,
    })),
  });
  const expenditureResults = useQueries({
    queries: members.map(member => ({
      queryKey: ['clients', member.clientId, 'expenditures'],
      enabled: !!member.clientId,
    })),
  });
  const lifeEventResults = useQueries({
    queries: members.map(member => ({
      queryKey: ['clients', member.clientId, 'life-events'],
      enabled: !!member.clientId,
    })),
  });

  const allResults = [...incomeResults, ...expenditureResults, ...lifeEventResults];
  const isLoading = allResults.some(result => result.isLoading);
  const hasErrors = allResults.some(result => result.isError);

  const incomes = aggregateFamilyIncomes(
    members,
    incomeResults.map(result => result.data as ApiIncome[] | undefined),
  );
  const expenditures = aggregateFamilyExpenditures(
    members,
    expenditureResults.map(result => result.data as ApiExpenditure[] | undefined),
  );
  const lifeEvents = aggregateFamilyLifeEvents(
    members,
    lifeEventResults.map(result => result.data as LifeEvent[] | undefined),
  );

  return { incomes, expenditures, lifeEvents, isLoading, hasErrors };
}
